import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import {
  Building2,
  FileText,
  Plus,
  Search,
  User,
  AlertTriangle,
  Eye,
  Pencil,
  Trash2,
  Paperclip,
  Download,
  Loader2,
  X,
} from 'lucide-react';
import { DailyLogFormResponsive } from '../DailyLogFormResponsive';
import { useAuth } from '../../../src/hooks/useAuth';
import { useDailyLogs } from '../../../src/hooks/queries/use-daily-logs';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { dailyLogService, type DailyLogActivity } from '../../../src/services/daily-log.service';
import { documentService, type Document } from '../../../src/services/document.service';
import { fileStorageService } from '../../../src/services/file-storage.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useConfirmDialog } from '../../ui/confirm-dialog';

export const getWeatherIcon = (weather: string) => {
  switch ((weather || '').toLowerCase()) {
    case 'clear': return '☀️';
    case 'partly cloudy': return '⛅';
    case 'cloudy': return '☁️';
    case 'light rain': return '🌦️';
    case 'heavy rain': return '🌧️';
    case 'snow': return '❄️';
    case 'windy': return '💨';
    default: return '🌤️';
  }
};

export const getWeatherColor = (weather: string) => {
  switch ((weather || '').toLowerCase()) {
    case 'clear': return 'text-yellow-600';
    case 'partly cloudy': return 'text-blue-400';
    case 'cloudy': return 'text-gray-500';
    case 'light rain': return 'text-blue-600';
    case 'heavy rain': return 'text-blue-800';
    case 'snow': return 'text-blue-200';
    case 'windy': return 'text-gray-400';
    default: return 'text-gray-600';
  }
};

// ─── View Log Dialog ─────────────────────────────────────────────────────────

function ViewLogDialog({ log, projectName, onClose }: { log: any; projectName: string; onClose: () => void }) {
  const [activities, setActivities] = useState<DailyLogActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [attachments, setAttachments] = useState<Document[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    dailyLogService.getDailyLogActivities(log.daily_log_id)
      .then(res => setActivities(Array.isArray(res.data) ? res.data : []))
      .catch(() => setActivities([]))
      .finally(() => setLoadingActivities(false));
  }, [log.daily_log_id]);

  const loadAttachments = async () => {
    if (attachmentsLoaded) return;
    setLoadingAttachments(true);
    try {
      const res = await documentService.getDocuments(1, 100, {
        organization_id: log.organization_id,
        project_id: log.project_id,
        document_type: 'photo',
      });
      const all: Document[] = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? [];
      // Prefer photos whose document_number matches DL-{log_date}
      const dated = all.filter(d => (d as any).document_number?.startsWith(`DL-${log.log_date}`));
      setAttachments(dated.length > 0 ? dated : all);
    } catch {
      // silently fail — show empty state
    } finally {
      setLoadingAttachments(false);
      setAttachmentsLoaded(true);
    }
  };

  const handleDownload = async (doc: Document) => {
    setDownloadingId(doc.document_id);
    try {
      const res = doc.storage_id
        ? await fileStorageService.downloadFile(doc.storage_id)
        : await documentService.downloadDocument(doc.document_id);
      if (res.success && res.data) {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_storage?.file_name || doc.document_name || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      showError({ title: 'Download failed', message: e.message });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (doc: Document) => {
    try {
      const res = doc.storage_id
        ? await fileStorageService.previewFile(doc.storage_id)
        : await documentService.previewDocument(doc.document_id);
      if (res.success && res.data) {
        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank');
      }
    } catch (e: any) {
      showError({ title: 'Preview failed', message: e.message });
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[720px] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {log.log_title || `Log #${log.log_number || log.daily_log_id?.slice(0, 8)}`}
          </DialogTitle>
          <DialogDescription>
            {projectName} · {log.log_date}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Key Facts Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {log.foreman && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Foreman</p>
                <p className="font-medium">{log.foreman}</p>
              </div>
            )}
            {log.total_workers_on_site != null && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Crew on Site</p>
                <p className="font-medium">{log.total_workers_on_site} workers</p>
              </div>
            )}
            {log.work_completed_percentage != null && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Completion</p>
                <p className="font-medium">{Number(log.work_completed_percentage).toFixed(0)}%</p>
              </div>
            )}
            {log.weather_conditions && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Weather</p>
                <p className="font-medium">{getWeatherIcon(log.weather_conditions)} {log.weather_conditions}</p>
              </div>
            )}
            {log.temperature_high != null && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Temperature</p>
                <p className="font-medium">
                  {log.temperature_high}°C{log.temperature_low != null ? ` / ${log.temperature_low}°C` : ''}
                </p>
              </div>
            )}
            {log.wind_conditions && (
              <div className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-0.5">Wind</p>
                <p className="font-medium">{log.wind_conditions}</p>
              </div>
            )}
            {log.contractors_on_site && (
              <div className="bg-muted/40 rounded-lg p-3 sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-0.5">Contractors on Site</p>
                <p className="font-medium">{log.contractors_on_site}</p>
              </div>
            )}
          </div>

          {/* Status Badges */}
          {(log.safety_incidents || log.safety_meetings_held || log.quality_issues) && (
            <div className="flex flex-wrap gap-2">
              {log.safety_incidents && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">⚠️ Safety Incident</Badge>
              )}
              {log.safety_meetings_held && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">✅ Safety Meeting Held</Badge>
              )}
              {log.quality_issues && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">🔍 Quality Issue</Badge>
              )}
            </div>
          )}

          {/* Work Performed */}
          {log.work_performed && (
            <div>
              <h4 className="text-sm font-semibold mb-1">Work Performed</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{log.work_performed}</p>
            </div>
          )}

          {/* Site Conditions */}
          {log.site_conditions && (
            <div>
              <h4 className="text-sm font-semibold mb-1">Site Conditions</h4>
              <p className="text-sm text-muted-foreground">{log.site_conditions}</p>
            </div>
          )}

          {/* Activities Conducted */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              Activities Conducted
              {loadingActivities && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </h4>
            {!loadingActivities && activities.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No activities recorded for this log.</p>
            ) : (
              <div className="space-y-3">
                {activities.map((act, idx) => (
                  <div key={act.activity_id || idx} className="border rounded-lg p-3 bg-muted/20">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-sm font-medium">
                          {act.activity_title || act.activity_description}
                        </p>
                        {act.activity_title && act.activity_description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{act.activity_description}</p>
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {act.is_completed && (
                          <Badge className="bg-green-100 text-green-800 text-xs">Done</Badge>
                        )}
                        {act.is_delayed && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">Delayed</Badge>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {act.location_area && <span>📍 {act.location_area}</span>}
                      {act.workers_assigned != null && <span>👥 {act.workers_assigned} workers</span>}
                      {act.hours_worked != null && <span>⏱ {act.hours_worked}h worked</span>}
                      {act.percentage_complete != null && <span>📊 {act.percentage_complete}% complete</span>}
                      {act.quantity_completed != null && (
                        <span>📦 {act.quantity_completed} {act.unit_of_measure || ''}</span>
                      )}
                      {act.start_time && act.end_time && (
                        <span>🕐 {act.start_time} – {act.end_time}</span>
                      )}
                      {act.equipment_used && <span className="sm:col-span-2">🔧 {act.equipment_used}</span>}
                    </div>
                    {act.materials_consumed && (
                      <p className="text-xs text-muted-foreground mt-1">Materials: {act.materials_consumed}</p>
                    )}
                    {act.delay_reason && (
                      <p className="text-xs text-orange-700 mt-1">Delay reason: {act.delay_reason}</p>
                    )}
                    {act.quality_notes && (
                      <p className="text-xs text-blue-700 mt-1">Quality: {act.quality_notes}</p>
                    )}
                    {act.safety_notes && (
                      <p className="text-xs text-red-700 mt-1">Safety: {act.safety_notes}</p>
                    )}
                    {act.notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic">{act.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scheduled / Completed / Delayed Activities (log-level text) */}
          {(log.scheduled_activities || log.completed_activities || log.delayed_activities) && (
            <div className="space-y-2">
              {log.scheduled_activities && (
                <div>
                  <span className="text-sm font-semibold">Scheduled: </span>
                  <span className="text-sm text-muted-foreground">{log.scheduled_activities}</span>
                </div>
              )}
              {log.completed_activities && (
                <div>
                  <span className="text-sm font-semibold">Completed: </span>
                  <span className="text-sm text-muted-foreground">{log.completed_activities}</span>
                </div>
              )}
              {log.delayed_activities && (
                <div>
                  <span className="text-sm font-semibold">Delayed: </span>
                  <span className="text-sm text-muted-foreground">{log.delayed_activities}</span>
                </div>
              )}
            </div>
          )}

          {/* Materials & Equipment */}
          {(log.areas_worked || log.materials_delivered || log.materials_used || log.material_shortages || log.equipment_on_site || log.equipment_issues) && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Site Resources</h4>
              {log.areas_worked && (
                <div><span className="text-sm font-medium">Areas: </span><span className="text-sm text-muted-foreground">{log.areas_worked}</span></div>
              )}
              {log.materials_delivered && (
                <div><span className="text-sm font-medium">Materials Delivered: </span><span className="text-sm text-muted-foreground">{log.materials_delivered}</span></div>
              )}
              {log.materials_used && (
                <div><span className="text-sm font-medium">Materials Used: </span><span className="text-sm text-muted-foreground">{log.materials_used}</span></div>
              )}
              {log.material_shortages && (
                <div><span className="text-sm font-medium text-orange-700">Shortages: </span><span className="text-sm text-orange-600">{log.material_shortages}</span></div>
              )}
              {log.equipment_on_site && (
                <div><span className="text-sm font-medium">Equipment: </span><span className="text-sm text-muted-foreground">{log.equipment_on_site}</span></div>
              )}
              {log.equipment_issues && (
                <div><span className="text-sm font-medium text-orange-700">Equipment Issues: </span><span className="text-sm text-orange-600">{log.equipment_issues}</span></div>
              )}
            </div>
          )}

          {/* Visitors / Meetings */}
          {(log.visitors_on_site || log.meetings_held) && (
            <div className="space-y-1">
              {log.visitors_on_site && (
                <div><span className="text-sm font-medium">Visitors: </span><span className="text-sm text-muted-foreground">{log.visitors_on_site}</span></div>
              )}
              {log.meetings_held && (
                <div><span className="text-sm font-medium">Meetings: </span><span className="text-sm text-muted-foreground">{log.meetings_held}</span></div>
              )}
              {log.inspections_conducted && (
                <div><span className="text-sm font-medium">Inspections: </span><span className="text-sm text-muted-foreground">{log.inspections_conducted}</span></div>
              )}
            </div>
          )}

          {/* Issues */}
          {(log.problems_encountered || log.reason_for_delays) && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm font-semibold text-orange-800 mb-1">Issues Reported</p>
              {log.problems_encountered && <p className="text-sm text-orange-700">{log.problems_encountered}</p>}
              {log.reason_for_delays && <p className="text-sm text-orange-700 mt-1">Delay reason: {log.reason_for_delays}</p>}
            </div>
          )}

          {/* Corrective Actions / Follow-up */}
          {(log.corrective_actions || log.follow_up_required) && (
            <div className="space-y-2">
              {log.corrective_actions && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Corrective Actions</h4>
                  <p className="text-sm text-muted-foreground">{log.corrective_actions}</p>
                </div>
              )}
              {log.follow_up_required && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Follow-up Required</h4>
                  <p className="text-sm text-muted-foreground">{log.follow_up_required}</p>
                </div>
              )}
            </div>
          )}

          {/* Attachments */}
          {(log.photos_taken ?? 0) > 0 && (
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <Paperclip className="h-4 w-4" />
                  Attachments ({log.photos_taken})
                </h4>
                {!attachmentsLoaded && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadAttachments} disabled={loadingAttachments}>
                    {loadingAttachments && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {loadingAttachments ? 'Loading…' : 'Load Attachments'}
                  </Button>
                )}
              </div>
              {attachmentsLoaded && attachments.length === 0 && (
                <p className="text-xs text-muted-foreground">No attachment records found for this log.</p>
              )}
              {attachments.map(doc => (
                <div key={doc.document_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate">
                      {doc.file_storage?.file_name || doc.document_name || doc.document_id.slice(0, 8)}
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <button
                      title="Preview"
                      onClick={() => handleView(doc)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button
                      title="Download"
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.document_id}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
                    >
                      {downloadingId === doc.document_id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Download className="h-3.5 w-3.5" />
                      }
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Log Action Row ───────────────────────────────────────────────────────────

function LogActions({
  log,
  onView,
  onEdit,
  onDelete,
}: {
  log: any;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-1 shrink-0 ml-2">
      <button
        title="View details"
        onClick={onView}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <Eye className="h-4 w-4" />
      </button>
      <button
        title="Edit log"
        onClick={onEdit}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <Pencil className="h-4 w-4" />
      </button>
      {(log.photos_taken ?? 0) > 0 && (
        <button
          title={`${log.photos_taken} attachment${log.photos_taken !== 1 ? 's' : ''} — view to access`}
          onClick={onView}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors relative"
        >
          <Paperclip className="h-4 w-4" />
          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary text-[9px] text-primary-foreground flex items-center justify-center font-bold leading-none">
            {log.photos_taken > 9 ? '9+' : log.photos_taken}
          </span>
        </button>
      )}
      <button
        title="Delete log"
        onClick={onDelete}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-red-50 hover:text-destructive transition-colors"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── Main DailyLogs Component ─────────────────────────────────────────────────

export function DailyLogs() {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');

  const [viewingLog, setViewingLog] = useState<any | null>(null);
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { showConfirm, ConfirmDialog } = useConfirmDialog();

  const { orgId } = useAuth();
  const { data: logs = [], isLoading, refetch } = useDailyLogs(orgId);
  const { data: projects = [] } = useProjects(orgId);

  const todaysLogs = logs.filter((log: any) => log.log_date === today);

  const filteredLogs = logs.filter((log: any) => {
    const matchesDate = selectedDate === 'all' || log.log_date === selectedDate;
    const projectName = (projects as any[]).find((p: any) => p.project_id === log.project_id)?.project_name || '';
    const searchText = `${log.work_performed || ''} ${log.log_title || ''} ${projectName} ${log.foreman || ''}`.toLowerCase();
    const matchesSearch = !searchQuery || searchText.includes(searchQuery.toLowerCase());
    const matchesProject = projectFilter === 'all' || log.project_id === projectFilter;
    return matchesDate && matchesSearch && matchesProject;
  });

  const totalCrew = logs.reduce((sum: number, log: any) => sum + (log.total_workers_on_site || 0), 0);
  const issuesCount = logs.filter((log: any) => log.problems_encountered || log.delayed_activities).length;

  const getProjectName = (log: any) =>
    (projects as any[]).find((p: any) => p.project_id === log.project_id)?.project_name || 'Unknown Project';

  const handleDelete = (log: any) => {
    showConfirm({
      title: 'Delete daily log',
      description: `Delete "${log.log_title || `Log #${log.log_number}`}" from ${log.log_date}? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await dailyLogService.deleteDailyLog(log.daily_log_id);
          showSuccess({ title: 'Deleted', message: 'Daily log removed successfully' });
          refetch();
        } catch (e: any) {
          showError({ title: 'Delete failed', message: e.message });
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <ConfirmDialog />

      {/* View log dialog */}
      {viewingLog && (
        <ViewLogDialog
          log={viewingLog}
          projectName={getProjectName(viewingLog)}
          onClose={() => setViewingLog(null)}
        />
      )}

      {/* Edit log modal (DailyLogFormResponsive in edit mode) */}
      {editOpen && editingLogId && (
        <DailyLogFormResponsive
          mode="edit"
          dailyLogId={editingLogId}
          onSuccess={() => { setEditOpen(false); setEditingLogId(null); refetch(); setSelectedDate('all'); }}
          trigger={<span />}
          externalOpen={editOpen}
          onOpenChange={(open) => { if (!open) { setEditOpen(false); setEditingLogId(null); } }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Daily Logs</h2>
          <p className="text-muted-foreground">Record and track daily construction activities</p>
        </div>
        <DailyLogFormResponsive
          mode="create"
          onSuccess={() => { refetch(); setSelectedDate('all'); }}
          trigger={
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Log Entry
            </Button>
          }
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Total Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">
              {isLoading ? '—' : logs.length}
            </div>
            <p className="text-xs text-blue-600 mt-1">Log entries recorded</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-green-600" />
              Projects with Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {isLoading ? '—' : new Set(logs.map((l: any) => l.project_id)).size}
            </div>
            <p className="text-xs text-green-600 mt-1">Projects with activity</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-purple-600" />
              Total Workers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {isLoading ? '—' : totalCrew}
            </div>
            <p className="text-xs text-purple-600 mt-1">Cumulative workers on site</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              Issues Reported
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-700">
              {isLoading ? '—' : issuesCount}
            </div>
            <p className="text-xs text-orange-600 mt-1">Logs with issues or delays</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Activities Highlight */}
      {todaysLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Today's Activities
            </CardTitle>
            <CardDescription>{new Date().toLocaleDateString('en-US', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {todaysLogs.map((log: any) => {
                const projectName = getProjectName(log);
                const hasIssues = log.problems_encountered || log.delayed_activities;
                return (
                  <div key={log.daily_log_id} className="border rounded-lg p-4 bg-primary/5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium">{log.log_title || `Log #${log.log_number || log.daily_log_id?.slice(0, 8)}`}</h4>
                        <Badge variant="outline">{projectName}</Badge>
                        {log.work_completed_percentage != null && (
                          <Badge variant="secondary">{Number(log.work_completed_percentage).toFixed(0)}% complete</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {log.foreman && (
                          <span className="text-sm text-muted-foreground">👷 {log.foreman}</span>
                        )}
                        <LogActions
                          log={log}
                          onView={() => setViewingLog(log)}
                          onEdit={() => { setEditingLogId(log.daily_log_id); setEditOpen(true); }}
                          onDelete={() => handleDelete(log)}
                        />
                      </div>
                    </div>
                    {log.work_performed && (
                      <p className="text-sm text-muted-foreground mb-3">{log.work_performed}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {log.weather_conditions && (
                        <Badge variant="outline">
                          {getWeatherIcon(log.weather_conditions)} {log.weather_conditions}
                        </Badge>
                      )}
                      {log.temperature_high != null && (
                        <Badge variant="outline">🌡️ {log.temperature_high}°C</Badge>
                      )}
                      {log.total_workers_on_site != null && (
                        <Badge variant="outline">👥 {log.total_workers_on_site} workers</Badge>
                      )}
                      {log.areas_worked && (
                        <Badge variant="outline">📍 {log.areas_worked}</Badge>
                      )}
                    </div>
                    {hasIssues && (
                      <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                        <span className="text-sm font-medium text-orange-800">Issues: </span>
                        <span className="text-sm text-orange-700">
                          {log.problems_encountered || log.delayed_activities}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Log List */}
      <Card>
        <CardHeader>
          <CardTitle>All Log Entries</CardTitle>
          <CardDescription>View and search historical daily logs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search activities, projects, or foreman..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="date-filter" className="text-xs">Date</Label>
              <div className="flex gap-2 items-center">
                <Select value={selectedDate} onValueChange={setSelectedDate}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    <SelectItem value={today}>Today</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  className="w-40"
                  value={selectedDate === 'all' ? '' : selectedDate}
                  onChange={(e) => e.target.value ? setSelectedDate(e.target.value) : setSelectedDate('all')}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="project-filter" className="text-xs">Project</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {(projects as any[]).map((p: any) => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      {p.project_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading daily logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No log entries found</h3>
              <p className="text-muted-foreground">
                {searchQuery || projectFilter !== 'all' || selectedDate !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first daily log to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredLogs.map((log: any) => {
                const projectName = getProjectName(log);
                const hasIssues = log.problems_encountered || log.delayed_activities;
                return (
                  <div key={log.daily_log_id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="font-medium">
                          {log.log_title || `Log #${log.log_number || log.daily_log_id?.slice(0, 8)}`}
                        </h4>
                        <Badge variant="outline">{projectName}</Badge>
                        <Badge variant="secondary">{log.log_date}</Badge>
                        {log.work_completed_percentage != null && (
                          <Badge className="bg-green-100 text-green-800">
                            {Number(log.work_completed_percentage).toFixed(0)}% complete
                          </Badge>
                        )}
                      </div>
                      <LogActions
                        log={log}
                        onView={() => setViewingLog(log)}
                        onEdit={() => { setEditingLogId(log.daily_log_id); setEditOpen(true); }}
                        onDelete={() => handleDelete(log)}
                      />
                    </div>

                    {log.work_performed && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{log.work_performed}</p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-3">
                      {log.areas_worked && (
                        <div>
                          <span className="font-medium">Areas Worked:</span>
                          <p className="text-muted-foreground">{log.areas_worked}</p>
                        </div>
                      )}
                      {log.foreman && (
                        <div>
                          <span className="font-medium">Foreman:</span>
                          <p className="text-muted-foreground">{log.foreman}</p>
                        </div>
                      )}
                      {log.total_workers_on_site != null && (
                        <div>
                          <span className="font-medium">Crew Size:</span>
                          <p className="text-muted-foreground">{log.total_workers_on_site} workers</p>
                        </div>
                      )}
                    </div>

                    {log.materials_delivered && (
                      <div className="mb-2">
                        <span className="font-medium text-sm">Materials Delivered: </span>
                        <span className="text-sm text-muted-foreground">{log.materials_delivered}</span>
                      </div>
                    )}

                    {log.equipment_on_site && (
                      <div className="mb-2">
                        <span className="font-medium text-sm">Equipment: </span>
                        <span className="text-sm text-muted-foreground">{log.equipment_on_site}</span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-3">
                      {log.weather_conditions && (
                        <Badge variant="outline">
                          {getWeatherIcon(log.weather_conditions)} {log.weather_conditions}
                        </Badge>
                      )}
                      {log.temperature_high != null && (
                        <Badge variant="outline">🌡️ {log.temperature_high}°C</Badge>
                      )}
                      {log.safety_incidents && (
                        <Badge variant="outline" className="bg-red-50 text-red-700">⚠️ Safety Incident</Badge>
                      )}
                      {log.safety_meetings_held && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">✅ Safety Meeting</Badge>
                      )}
                      {log.quality_issues && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">🔍 Quality Issue</Badge>
                      )}
                      {(log.photos_taken ?? 0) > 0 && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          📎 {log.photos_taken} attachment{log.photos_taken !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>

                    {hasIssues && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-2">
                        <span className="font-medium text-orange-800">Issues: </span>
                        <span className="text-orange-700">
                          {log.problems_encountered || log.delayed_activities}
                        </span>
                      </div>
                    )}

                    {log.site_conditions && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <span className="font-medium">Site Conditions: </span>
                        <span className="text-muted-foreground">{log.site_conditions}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
